import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useEffect, useState } from 'react';
import { UserPlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Button } from '@follow/ui';

import TeamService from '../../services/TeamService';
import { KnownTeammate } from '../../services/models/team';
import { REGEX_EMAIL } from '../../services/models/user';
import { ApiError } from '../../types/common';

interface ModalInviteTeammateProps {
  visible: boolean;
  siteId: string;
  // The ceiling: when the owner has no rentals feature, no teammate can get it.
  ownerRentalEnabled: boolean;
  // Lower-cased emails already on the team (members + pending invites), so a
  // duplicate is caught before the round-trip.
  existingEmails: string[];
  onClose: () => void;
}

const inputClassName = 'w-full px-3 py-2 rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-20 bg-neutral-5 typo-small text-neutral focus:outline-primary-500';

const teammateName = (teammate: KnownTeammate) => (
  `${teammate.firstName ?? ''} ${teammate.lastName ?? ''}`.trim() || teammate.email
);

const errorMessages: Record<string, string> = {
  CANNOT_INVITE_OWNER: 'Cette personne est le propriétaire du site : elle a déjà tous les accès.',
  USER_IS_ADMIN: 'Ce compte est un administrateur Follow, il a déjà accès à ce site.',
  ALREADY_MEMBER: 'Cette personne fait déjà partie de l\'équipe.',
  ALREADY_INVITED: 'Une invitation est déjà en attente pour cet email.',
};

const ModalInviteTeammate: React.FC<ModalInviteTeammateProps> = ({
  visible,
  siteId,
  ownerRentalEnabled,
  existingEmails,
  onClose,
}) => {
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number>();
  const [rentalEnabled, setRentalEnabled] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [error, setError] = useState<string>();

  // Reset every time the modal opens so a previous attempt never leaks in.
  useEffect(() => {
    if (visible) {
      setEmail('');
      setDebounced('');
      setSelectedUserId(undefined);
      setRentalEnabled(false);
      setSuggestionsOpen(false);
      setError(undefined);
    }
  }, [visible]);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(email.trim()), 300);
    return () => clearTimeout(timer);
  }, [email]);

  // With no search term the API still returns the caller's existing teammates,
  // which makes the dropdown useful on focus alone.
  const { data, isFetching } = useQuery({
    queryKey: ['known-teammates', siteId, debounced],
    queryFn: () => TeamService.getKnownTeammates({
      search: debounced,
      excludeLocationId: siteId,
      limit: 6,
    }),
    enabled: visible,
  });

  const suggestions = data?.members ?? [];

  const inviteMutation = useMutation({
    mutationFn: () => TeamService.invite(siteId, {
      ...(selectedUserId != null ? { userId: selectedUserId } : { email: email.trim() }),
      rentalEnabled: ownerRentalEnabled && rentalEnabled,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', siteId] });
      queryClient.invalidateQueries({ queryKey: ['known-teammates'] });
      toast.success('Invitation envoyée.');
      onClose();
    },
    onError: (apiError: ApiError) => {
      const message = errorMessages[apiError.code ?? ''] ?? apiError.message ?? 'Une erreur est survenue.';
      setError(message);
    },
  });

  const onEmailChange = (value: string) => {
    setEmail(value);
    // Editing the field breaks the link with the picked teammate.
    setSelectedUserId(undefined);
    setSuggestionsOpen(true);
    setError(undefined);
  };

  const selectTeammate = (teammate: KnownTeammate) => {
    setEmail(teammate.email);
    setSelectedUserId(teammate.userId);
    setSuggestionsOpen(false);
    setError(undefined);
  };

  const onSubmit = () => {
    const trimmed = email.trim();

    if (selectedUserId == null) {
      if (!REGEX_EMAIL.test(trimmed)) {
        setError('Veuillez saisir une adresse email valide.');
        return;
      }
      if (existingEmails.includes(trimmed.toLowerCase())) {
        setError('Cette personne fait déjà partie de l\'équipe.');
        return;
      }
    }

    inviteMutation.mutate();
  };

  const busy = inviteMutation.isPending;

  return (
    <Transition.Root show={visible} as={Fragment}>
      {/* eslint-disable-next-line @typescript-eslint/no-empty-function */}
      <Dialog as="div" className="relative z-50" onClose={!busy ? onClose : () => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-neutral-30 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              {/* overflow-visible (not the usual overflow-hidden): the
                  suggestions dropdown has to escape the panel. */}
              <Dialog.Panel className="relative transform overflow-visible rounded-xl bg-white p-6 text-left shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100">
                      <UserPlusIcon className="h-5 w-5 text-primary-600" />
                    </div>
                    <Dialog.Title as="h3" className="text-neutral typo-h3">
                      Inviter des membres
                    </Dialog.Title>
                  </div>
                  <button
                    type="button"
                    className="rounded-md p-1 text-neutral-40 hover:text-neutral hover:bg-neutral-5 transition-colors focus:outline-none disabled:opacity-50"
                    onClick={onClose}
                    disabled={busy}
                  >
                    <span className="sr-only">Fermer</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>

                <div className="flex flex-col gap-5">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="teammate-email" className="block typo-small text-neutral-60">
                      Email
                    </label>
                    <div className="relative">
                      <input
                        id="teammate-email"
                        type="email"
                        autoComplete="off"
                        value={email}
                        onChange={(e) => onEmailChange(e.target.value)}
                        onFocus={() => setSuggestionsOpen(true)}
                        onBlur={() => setTimeout(() => setSuggestionsOpen(false), 150)}
                        placeholder="prenom.nom@example.com"
                        className={inputClassName}
                      />
                      {suggestionsOpen && (isFetching || suggestions.length > 0) && (
                        <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-md bg-white shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10">
                          {isFetching && suggestions.length === 0 && (
                            <div className="px-3 py-2 typo-small text-neutral-40">Recherche…</div>
                          )}
                          {suggestions.map((teammate) => (
                            <button
                              key={teammate.userId}
                              type="button"
                              // Keep the input focused so the click lands before the blur.
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => selectTeammate(teammate)}
                              className="w-full text-left px-3 py-2 hover:bg-neutral-5 transition-colors"
                            >
                              <div className="typo-small text-neutral">{teammateName(teammate)}</div>
                              <div className="typo-small text-neutral-50">
                                {teammate.email}
                                {teammate.locationCount > 0 && ` · ${teammate.locationCount} site${teammate.locationCount > 1 ? 's' : ''}`}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="typo-caption text-neutral-40">
                      Choisissez un coéquipier existant ou saisissez une nouvelle adresse : nous lui enverrons une invitation.
                    </p>
                  </div>

                  {ownerRentalEnabled ? (
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-neutral-20 text-primary-500 focus:ring-primary-500 cursor-pointer"
                        checked={rentalEnabled}
                        onChange={(e) => setRentalEnabled(e.target.checked)}
                      />
                      <span className="flex flex-col">
                        <span className="typo-small text-neutral !font-semibold">Accès aux réservations</span>
                        <span className="typo-small text-neutral-60">
                          Ce membre pourra consulter et gérer les réservations et les équipements de ce site.
                        </span>
                      </span>
                    </label>
                  ) : (
                    <div className="p-3 rounded-lg bg-neutral-5 outline outline-1 outline-offset-[-1px] outline-neutral-10">
                      <p className="typo-small text-neutral-60">
                        Les réservations ne sont pas activées sur votre compte : vos coéquipiers ne peuvent donc pas y accéder.
                      </p>
                    </div>
                  )}

                  {error && (
                    <p className="typo-small text-error-600">{error}</p>
                  )}
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
                    Annuler
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    tone="light"
                    onClick={onSubmit}
                    loading={busy}
                    disabled={busy || email.trim().length === 0}
                  >
                    Envoyer l'invitation
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ModalInviteTeammate;
